import React from 'react';
import { useNavigate } from 'react-router-dom';
import { EventRepeat, CreditCard, CalendarToday } from '@mui/icons-material';
import {
  styleThousandCommas,
  planRecurrenceMap,
  getSubscriptionStatusColor,
  formatSubscriptionStatusLabel,
  isSubscriptionCancelAtPeriodEnd,
  resolveSubscriptionChargeDate,
  formatSubscriptionPlanTypeLabel,
  formatSubscriptionResourceTypeLabel,
  formatSubscriptionDate,
} from '../../utils/helpers';
import { SubscriptionSchema } from '../../data/types';

type SubscriptionCardProps = {
  subscription: SubscriptionSchema;
  /** Opens community resources modal when community_id is present */
  onViewResources?: (communityId: string) => void;
};

/**
 * Formats payment_method for display. Backend returns labels like "Visa •••• 4242";
 * still guards against raw Stripe IDs if one slips through.
 */
const formatPaymentMethodLabel = (paymentMethod?: string): string => {
  if (!paymentMethod) return '';
  if (paymentMethod.startsWith('pm_') || paymentMethod.startsWith('card_')) {
    return 'Card on file';
  }
  return paymentMethod;
};

/**
 * Desktop billing-focused card for a recurring (weekly/monthly/annual) subscription.
 * Emphasizes amount, cadence, and renewal timeline rather than membership-style chips.
 */
const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  subscription,
  onViewResources,
}) => {
  const navigate = useNavigate();
  const statusColors = getSubscriptionStatusColor(subscription.status || 'unknown');
  const cancelsAtPeriodEnd = isSubscriptionCancelAtPeriodEnd(subscription.cancel_at_period_end);
  const recurrenceLabel =
    subscription.plan_recurrence &&
    planRecurrenceMap[subscription.plan_recurrence as keyof typeof planRecurrenceMap];
  const chargeDate = resolveSubscriptionChargeDate(subscription);
  const communityName = subscription.name || 'Community';
  const planTitle = subscription.plan_name || 'Subscription';
  const planTypeLabel = formatSubscriptionPlanTypeLabel(subscription.plan_type);
  const resourceTypeLabel = formatSubscriptionResourceTypeLabel(subscription.resource_type);
  const paymentLabel = formatPaymentMethodLabel(subscription.payment_method);
  const associatedResourceNames = (subscription.associated_resources || [])
    .map((resource) => resource.resource_name)
    .filter(Boolean) as string[];

  const coverageParts = [
    subscription.plan_type !== 'membership' ? resourceTypeLabel : null,
    subscription.resource_name,
    subscription.is_fulltime_hours
      ? '24/7 Access'
      : (subscription.plan_max_hours != null && subscription.plan_type !== 'membership')
        ? `Up to ${styleThousandCommas(subscription.plan_max_hours)} hrs`
        : null,
    planTypeLabel || null,
  ].filter(Boolean);

  return (
    <div className="border border-[#E9E9E9] rounded-xl overflow-hidden bg-white">
      <div className="flex flex-col lg:flex-row gap-6 p-5 lg:p-6">
        {/* Identity */}
        <div className="flex items-start gap-3 lg:w-[220px] flex-shrink-0">
          <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-[#F7FBFC] rounded-lg border border-[#E9E9E9]">
            {subscription.logo ? (
              <img
                src={subscription.logo}
                alt={communityName}
                className="h-8 w-8 object-contain"
              />
            ) : (
              <span className="font-raleway font-bold text-base text-primaryDark-0">
                {communityName.charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <div className="min-w-0">
            <p className="font-raleway font-semibold text-sm text-black truncate">{communityName}</p>
            <p className="font-open-sans text-xs text-[#8A8A8A] mt-0.5 truncate">{planTitle}</p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {subscription.status && (
                <span
                  className="font-raleway font-semibold text-[11px] px-2 py-0.5 rounded-md"
                  style={{
                    backgroundColor: statusColors.bgColor,
                    color: statusColors.primaryTextColor,
                  }}
                >
                  {formatSubscriptionStatusLabel(subscription.status)}
                </span>
              )}
              {cancelsAtPeriodEnd && (
                <span className="font-raleway font-semibold text-[11px] px-2 py-0.5 rounded-md bg-[#faeeee] text-[#CE5D5D]">
                  Ends this period
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Billing hero + timeline */}
        <div className="flex-1 min-w-0 flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-x-8 gap-y-4">
            <div>
              <p className="text-[11px] uppercase tracking-wide font-raleway font-semibold text-[#8A8A8A] mb-1">
                Recurring amount
              </p>
              <p className="font-raleway font-bold text-2xl text-primaryDark-0 leading-none">
                ${styleThousandCommas(subscription.plan_rate || 0)}
                {recurrenceLabel && (
                  <span className="text-sm font-semibold text-[#4D4D4D] ml-1">
                    / {recurrenceLabel}
                  </span>
                )}
              </p>
            </div>

            <div className="flex items-start gap-2 min-w-[140px]">
              <EventRepeat className="!w-4 !h-4 mt-0.5 text-primaryDark-0/60" />
              <div>
                <p className="text-[11px] uppercase tracking-wide font-raleway font-semibold text-[#8A8A8A]">
                  {cancelsAtPeriodEnd ? 'Access through' : 'Next charge'}
                </p>
                <p className="font-raleway font-semibold text-sm text-black mt-0.5">{chargeDate}</p>
              </div>
            </div>

            {subscription.current_period_end && subscription.next_charge_date && !cancelsAtPeriodEnd && (
              <div>
                <p className="text-[11px] uppercase tracking-wide font-raleway font-semibold text-[#8A8A8A]">
                  Period ends
                </p>
                <p className="font-raleway font-semibold text-sm text-black mt-0.5">
                  {formatSubscriptionDate(subscription.current_period_end)}
                </p>
              </div>
            )}

            {subscription.last_charge_date && (
              <div>
                <p className="text-[11px] uppercase tracking-wide font-raleway font-semibold text-[#8A8A8A]">
                  Last charged
                </p>
                <p className="font-raleway font-semibold text-sm text-black mt-0.5">
                  {formatSubscriptionDate(subscription.last_charge_date)}
                </p>
              </div>
            )}

            {subscription.started && (
              <div className="flex items-start gap-2 min-w-[140px]">
                <CalendarToday className="!w-3.5 !h-3.5 mt-0.5 text-primaryDark-0/55" />
                <div>
                  <p className="text-[11px] uppercase tracking-wide font-raleway font-semibold text-[#8A8A8A]">
                    Subscribed since
                  </p>
                  <p className="font-raleway font-semibold text-sm text-black mt-0.5">
                    {formatSubscriptionDate(subscription.started)}
                  </p>
                </div>
              </div>
            )}
          </div>

          {coverageParts.length > 0 && (
            <p className="text-xs font-open-sans text-[#4D4D4D]">
              {coverageParts.join(' · ')}
            </p>
          )}

          {associatedResourceNames.length > 0 && (
            <p className="text-xs font-open-sans text-[#8A8A8A]">
              Includes <span className="text-primaryDark-400">{associatedResourceNames.join(', ')}</span>
            </p>
          )}

          {paymentLabel && (
            <div className="inline-flex items-center gap-1.5 w-fit rounded-md border border-[#E9E9E9] bg-[#FAFAFA] px-2.5 py-1 text-xs font-open-sans text-[#4D4D4D]">
              <CreditCard className="!w-3.5 !h-3.5 text-primaryDark-0/60" />
              <span className="font-medium">{paymentLabel}</span>
            </div>
          )}
        </div>

        {/* Actions — wallet-first, resources secondary */}
        <div className="flex flex-row lg:flex-col gap-2 flex-shrink-0 lg:items-stretch">
          <button
            type="button"
            className="font-raleway border-0 bg-primaryDark-0 text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-primaryDark-300"
            onClick={() => navigate('/wallet')}
          >
            Manage Billing
          </button>
          {subscription.community_id && onViewResources && (
            <button
              type="button"
              className="font-raleway bg-white border border-[#E9E9E9] text-[#4D4D4D] rounded-md px-4 py-2 text-sm font-semibold hover:bg-gray-50"
              onClick={() => onViewResources(subscription.community_id || '')}
            >
              View Resources
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionCard;
